<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

const props = defineProps({
  stats: {
    type: Object,
    default: null,
  },
  sourcesCount: {
    type: Number,
    default: 0,
  },
});

const embeddingPercent = computed(() => {
  if (!props.stats || !props.stats.total) return 0;
  return Math.round((props.stats.with_embedding / props.stats.total) * 100);
});
</script>

<template>
  <div
    v-if="stats"
    class="grid grid-cols-2 lg:grid-cols-4 gap-3"
  >
    <!-- Files -->
    <div class="flex items-center gap-3 rounded-lg border border-slate-100 dark:border-slate-700 bg-white dark:bg-slate-800/50 px-4 py-3">
      <div class="flex h-9 w-9 items-center justify-center rounded-lg bg-blue-50 dark:bg-blue-900/20">
        <span class="i-lucide-file-text text-blue-500 text-lg" />
      </div>
      <div>
        <p class="text-xs text-slate-500 dark:text-slate-400">
          {{ t('OMNI.DATA_SOURCES.TAB_SOURCES') }}
        </p>
        <p class="text-lg font-semibold text-slate-900 dark:text-slate-100">
          {{ sourcesCount }}
        </p>
      </div>
    </div>

    <!-- Entries -->
    <div class="flex items-center gap-3 rounded-lg border border-slate-100 dark:border-slate-700 bg-white dark:bg-slate-800/50 px-4 py-3">
      <div class="flex h-9 w-9 items-center justify-center rounded-lg bg-violet-50 dark:bg-violet-900/20">
        <span class="i-lucide-database text-violet-500 text-lg" />
      </div>
      <div>
        <p class="text-xs text-slate-500 dark:text-slate-400">
          {{ t('OMNI.DATA_SOURCES.TAB_ENTRIES') }}
        </p>
        <p class="text-lg font-semibold text-slate-900 dark:text-slate-100">
          {{ stats.total }}
        </p>
      </div>
    </div>

    <!-- Embeddings Progress -->
    <div class="flex items-center gap-3 rounded-lg border border-slate-100 dark:border-slate-700 bg-white dark:bg-slate-800/50 px-4 py-3">
      <div class="flex h-9 w-9 items-center justify-center rounded-lg bg-emerald-50 dark:bg-emerald-900/20">
        <span class="i-lucide-sparkles text-emerald-500 text-lg" />
      </div>
      <div class="flex-1 min-w-0">
        <p class="text-xs text-slate-500 dark:text-slate-400">
          {{ t('OMNI.DATA_SOURCES.EMBEDDING_PROGRESS') }}
        </p>
        <div class="flex items-center gap-2">
          <p class="text-lg font-semibold text-slate-900 dark:text-slate-100">
            {{ stats.with_embedding }}<span class="text-sm font-normal text-slate-400">/{{ stats.total }}</span>
          </p>
        </div>
        <div class="mt-1 h-1.5 w-full rounded-full bg-slate-100 dark:bg-slate-700">
          <div
            class="h-1.5 rounded-full transition-all duration-500"
            :class="embeddingPercent === 100 ? 'bg-emerald-500' : 'bg-amber-400'"
            :style="{ width: `${embeddingPercent}%` }"
          />
        </div>
      </div>
    </div>

    <!-- Pending -->
    <div class="flex items-center gap-3 rounded-lg border border-slate-100 dark:border-slate-700 bg-white dark:bg-slate-800/50 px-4 py-3">
      <div class="flex h-9 w-9 items-center justify-center rounded-lg bg-amber-50 dark:bg-amber-900/20">
        <span class="i-lucide-clock text-amber-500 text-lg" />
      </div>
      <div>
        <p class="text-xs text-slate-500 dark:text-slate-400">
          {{ t('OMNI.DATA_SOURCES.PENDING') }}
        </p>
        <p class="text-lg font-semibold text-slate-900 dark:text-slate-100">
          {{ stats.without_embedding }}
        </p>
      </div>
    </div>
  </div>
</template>
